/**
 * B632 (e2) — the guest's ADD sheet: more tickets for the booking's own
 * night, paid right here. Two steps in one sheet — PICK (a stepper per
 * priced ticket type, the summary once something is counted) and PAY (the
 * held total from the kernel, the provider's card well, PAY carrying the
 * total). Built from 650:165's sheet grammar (no frame draws the add yet).
 * Face only; `useMeAdd.ts` holds the calls.
 */
import type { ReactElement } from "react";
import { Banner, Button, RightSheet, Stepper } from "@transact/ui";
import { meAddCount, meAddSummary, type MeAddChoice } from "../lib/meAdd.js";

/** The most of one type a buyer adds online — a bigger party is the groups desk's. */
const MAX_PER_TYPE = 10;

export type MeAddStep =
  | { readonly kind: "pick" }
  | { readonly kind: "pay"; readonly total: string; readonly card: "mounted" | { readonly unavailable: string } }
  | { readonly kind: "confirming" };

export function MeAddSheet(props: {
  readonly open: boolean;
  /** The tenant's slot noun (B126). */
  readonly unit: string;
  /** "Fri 9 · 7:00 PM" — the booking's night. */
  readonly nightWords: string;
  /** undefined = still reading the ticket types. */
  readonly choices: readonly MeAddChoice[] | undefined;
  readonly counts: Readonly<Record<string, number>>;
  readonly currency: string;
  readonly step: MeAddStep;
  readonly busy?: boolean;
  readonly error?: string | undefined;
  readonly onCount: (variantId: string, n: number) => void;
  readonly onContinue: () => void;
  readonly onPay: () => void;
  readonly onClose: () => void;
}): ReactElement | null {
  const { step } = props;
  const count = meAddCount(props.counts);
  const summary = props.choices === undefined ? [] : meAddSummary(props.choices, props.counts, props.nightWords, props.currency);
  const picking = step.kind === "pick";
  const payShut = step.kind === "pay" && step.card !== "mounted";
  const actions =
    step.kind === "confirming" ? undefined : (
      <>
        <Button variant="neutral" onClick={props.onClose}>
          Cancel
        </Button>
        {picking ? (
          <Button
            variant="primary"
            busy={props.busy === true}
            disabled={count === 0}
            {...(count === 0 ? { describedBy: "me-add-why" } : {})}
            onClick={props.onContinue}
          >
            Continue
          </Button>
        ) : (
          <Button variant="primary" busy={props.busy === true} disabled={payShut} {...(payShut ? { describedBy: "me-add-card-why" } : {})} onClick={props.onPay}>
            {`Pay ${step.total}`}
          </Button>
        )}
      </>
    );
  return (
    <RightSheet
      id="me-add"
      open={props.open}
      kicker="Add tickets to this order"
      title={`More tickets for ${props.nightWords}`}
      lede={`They join your booking’s ${props.unit} and arrive as a separate order, paid now.`}
      onClose={props.onClose}
      redline="me-add"
      actions={actions}
      status={
        picking && count === 0 && props.choices !== undefined && props.choices.length > 0 ? (
          <span id="me-add-why">Pick how many tickets to add.</span>
        ) : step.kind === "pay" ? (
          "Your new tickets are held while you pay."
        ) : undefined
      }
    >
      {step.kind === "confirming" ? (
        <p className="me-change-quiet" role="status">
          Payment received — confirming your tickets…
        </p>
      ) : props.choices === undefined ? (
        <p className="me-change-quiet" role="status">
          Finding the tickets you can add…
        </p>
      ) : props.choices.length === 0 ? (
        <p className="me-change-quiet">There’s nothing to add online right now. Reply to your confirmation email and the box office will help.</p>
      ) : !picking ? null : (
        /* the hold fixes the counts: at PAY the summary says them, the steppers step aside */
        <ul className="me-add-types">
          {props.choices.map((c) => (
            <li className="me-add-type" key={c.variantId}>
              <span className="me-add-type-name">{c.name}</span>
              <span className="me-add-type-price">{c.price}</span>
              <Stepper
                label={c.name}
                value={props.counts[c.variantId] ?? 0}
                min={0}
                max={MAX_PER_TYPE}
                frame="sheet"
                decreaseLabel={`Fewer ${c.name}`}
                increaseLabel={`More ${c.name}`}
                onChange={(n) => props.onCount(c.variantId, n)}
              />
            </li>
          ))}
        </ul>
      )}
      {summary.length > 0 && step.kind !== "confirming" ? (
        <dl className="me-change-summary">
          {summary.map((r) => (
            <div className="me-paid-row" key={r.label}>
              <dt>{r.label}</dt>
              <dd>{r.value}</dd>
            </div>
          ))}
          {step.kind === "pay" ? (
            <div className="me-paid-row me-add-total">
              <dt>Total</dt>
              <dd>{step.total}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      {picking && summary.length > 0 ? <p className="me-add-note">Fees and tax are added at the next step.</p> : null}
      {step.kind === "pay" ? (
        step.card === "mounted" ? (
          <div className="me-add-card" id="me-add-card" data-redline="me-add-card" />
        ) : (
          <Banner tone="warn" id="me-add-card-why">{`The card form could not open here (${step.card.unavailable}). Reply to your confirmation email and the box office will help.`}</Banner>
        )
      ) : null}
      {props.error !== undefined ? <Banner tone="warn">{props.error}</Banner> : null}
    </RightSheet>
  );
}
